import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Stack, Paper, IconButton, TextField, Divider,
  CircularProgress, Switch, FormControlLabel, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  ArrowBack, Save, PlayArrow, Chat, CallSplit, CalendarMonth, SupportAgent,
  FlagCircle, Add, Delete,
} from '@mui/icons-material';
import ReactFlow, {
  Background, Controls, MiniMap, addEdge, applyNodeChanges, applyEdgeChanges,
  Connection, Edge, Node, NodeChange, EdgeChange, MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { api } from '../config/api';

// Tipos de nodo disponibles en la paleta (bloques del flujo)
const PALETA = [
  { tipo: 'inicio', label: 'Inicio', icon: <FlagCircle />, color: '#10B981', desc: 'Punto de partida del flujo' },
  { tipo: 'mensaje', label: 'Enviar mensaje', icon: <Chat />, color: '#25D366', desc: 'El agente envía un texto' },
  { tipo: 'condicion', label: 'Condición', icon: <CallSplit />, color: '#F59E0B', desc: 'Si el cliente dice X → rama' },
  { tipo: 'agendar', label: 'Agendar cita', icon: <CalendarMonth />, color: '#4285F4', desc: 'Crea evento en el calendario' },
  { tipo: 'handoff', label: 'Derivar a humano', icon: <SupportAgent />, color: '#EF4444', desc: 'Pasa a un asesor' },
];

const colorPorTipo = (t: string) => PALETA.find((p) => p.tipo === t)?.color || '#64748B';

const FlujoEditor: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [flujo, setFlujo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState<Node | null>(null);
  const [probarOpen, setProbarOpen] = useState(false);
  const [probarMsg, setProbarMsg] = useState('');
  const [probarResp, setProbarResp] = useState<string[]>([]);
  const idRef = useRef(1);

  useEffect(() => {
    api.get(`/flujos/${id}`).then((r) => {
      const def = r.data.flujo.definicion || { nodes: [], edges: [] };
      setFlujo(r.data.flujo);
      setNodes(def.nodes || []);
      setEdges(def.edges || []);
      const maxId = (def.nodes || []).reduce((m: number, n: any) => Math.max(m, parseInt(n.id) || 0), 0);
      idRef.current = maxId + 1;
    }).finally(() => setLoading(false));
  }, [id]);

  const onNodesChange = useCallback((c: NodeChange[]) => setNodes((ns) => applyNodeChanges(c, ns)), []);
  const onEdgesChange = useCallback((c: EdgeChange[]) => setEdges((es) => applyEdgeChanges(c, es)), []);
  const onConnect = useCallback((c: Connection) => setEdges((es) => addEdge({ ...c, markerEnd: { type: MarkerType.ArrowClosed }, animated: true }, es)), []);

  const agregarNodo = (tipo: string) => {
    const p = PALETA.find((x) => x.tipo === tipo)!;
    const nuevo: Node = {
      id: String(idRef.current++),
      position: { x: 180 + Math.random() * 120, y: 80 + Math.random() * 200 },
      data: { label: p.label, tipo, texto: '', palabraClave: '' },
      style: {
        borderLeft: `5px solid ${p.color}`, borderRadius: 10, padding: 8,
        background: '#fff', fontSize: 13, width: 180, boxShadow: '0 1px 4px rgba(0,0,0,.12)',
      },
    };
    setNodes((ns) => [...ns, nuevo]);
  };

  const actualizarNodo = (campo: string, valor: string) => {
    if (!sel) return;
    setNodes((ns) => ns.map((n) => n.id === sel.id ? { ...n, data: { ...n.data, [campo]: valor, label: campo === 'label' ? valor : n.data.label } } : n));
    setSel((s) => s ? { ...s, data: { ...s.data, [campo]: valor } } : s);
  };

  const borrarNodo = () => {
    if (!sel) return;
    setNodes((ns) => ns.filter((n) => n.id !== sel.id));
    setEdges((es) => es.filter((e) => e.source !== sel.id && e.target !== sel.id));
    setSel(null);
  };

  const guardar = async (activar?: boolean) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/flujos/${id}`, {
        definicion: { nodes, edges },
        ...(activar !== undefined ? { activo: activar } : {}),
      });
      setFlujo(data.flujo);
    } finally { setSaving(false); }
  };

  const probar = async () => {
    // Guardar antes de probar para que el backend tenga la última versión
    await api.put(`/flujos/${id}`, { definicion: { nodes, edges } });
    const { data } = await api.post(`/flujos/${id}/probar`, { mensaje: probarMsg });
    setProbarResp(data.mensajes || []);
  };

  if (loading) return <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ height: { xs: 'auto', md: 'calc(100vh - 120px)' }, minHeight: { xs: '80vh', md: 'auto' }, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate('/app/flujos')}><ArrowBack /></IconButton>
          <Typography variant="h5" noWrap>{flujo?.nombre}</Typography>
          <Chip size="small" label={flujo?.activo ? 'Activo' : 'Inactivo'} color={flujo?.activo ? 'success' : 'default'} />
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <FormControlLabel control={<Switch checked={!!flujo?.activo} onChange={(e) => guardar(e.target.checked)} />} label="Activo" />
          <Button variant="outlined" startIcon={<PlayArrow />} onClick={() => { setProbarResp([]); setProbarOpen(true); }}>Probar</Button>
          <Button variant="contained" startIcon={<Save />} onClick={() => guardar()} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
        </Stack>
      </Stack>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, gap: 1, minHeight: 0 }}>
        {/* Paleta de bloques: columna en desktop, fila con scroll en móvil */}
        <Paper sx={{ width: { xs: '100%', md: 210 }, flexShrink: 0, p: 1.5, overflowX: 'auto' }}>
          <Typography variant="overline" color="text.secondary">Bloques</Typography>
          <Stack direction={{ xs: 'row', md: 'column' }} spacing={1} sx={{ mt: 1, overflowX: { xs: 'auto', md: 'visible' } }}>
            {PALETA.map((p) => (
              <Tooltip key={p.tipo} title={p.desc} placement="right">
                <Button variant="outlined" startIcon={p.icon} onClick={() => agregarNodo(p.tipo)}
                  sx={{ flexShrink: 0, justifyContent: 'flex-start', whiteSpace: 'nowrap', borderColor: p.color, color: p.color, '&:hover': { borderColor: p.color, bgcolor: `${p.color}11` } }}>
                  {p.label}
                </Button>
              </Tooltip>
            ))}
          </Stack>
          <Divider sx={{ my: 2, display: { xs: 'none', md: 'block' } }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
            Agrega bloques y conéctalos arrastrando desde el borde de uno a otro.
          </Typography>
        </Paper>

        {/* Lienzo */}
        <Paper sx={{ flex: 1, minWidth: 0, minHeight: { xs: 360, md: 'auto' } }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, n) => setSel(n)}
            onPaneClick={() => setSel(null)}
            fitView
          >
            <Background />
            <Controls />
            <MiniMap nodeColor={(n) => colorPorTipo(n.data?.tipo)} />
          </ReactFlow>
        </Paper>

        {/* Panel de propiedades del nodo seleccionado */}
        {sel && (
          <Paper sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, p: 2, overflowY: 'auto' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Bloque: {sel.data.tipo}</Typography>
              <IconButton size="small" color="error" onClick={borrarNodo}><Delete fontSize="small" /></IconButton>
            </Stack>
            <TextField fullWidth size="small" label="Nombre del bloque" sx={{ mb: 2 }} value={sel.data.label || ''} onChange={(e) => actualizarNodo('label', e.target.value)} />
            {(sel.data.tipo === 'mensaje') && (
              <TextField fullWidth multiline rows={4} label="Texto a enviar" value={sel.data.texto || ''} onChange={(e) => actualizarNodo('texto', e.target.value)} />
            )}
            {(sel.data.tipo === 'condicion') && (
              <TextField fullWidth label="Palabra/frase clave" helperText="Si el cliente escribe esto, sigue por aquí" value={sel.data.palabraClave || ''} onChange={(e) => actualizarNodo('palabraClave', e.target.value)} />
            )}
            {(sel.data.tipo === 'agendar') && (
              <Typography variant="body2" color="text.secondary">Este bloque crea una cita en el Google Calendar conectado del dueño.</Typography>
            )}
            {(sel.data.tipo === 'handoff') && (
              <Typography variant="body2" color="text.secondary">Este bloque deriva la conversación a un asesor humano.</Typography>
            )}
            {(sel.data.tipo === 'inicio') && (
              <Typography variant="body2" color="text.secondary">Punto de inicio del flujo. Conéctalo al primer bloque.</Typography>
            )}
          </Paper>
        )}
      </Box>

      {/* Diálogo de prueba del flujo */}
      <Dialog open={probarOpen} onClose={() => setProbarOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Probar flujo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Escribe un mensaje como si fueras un cliente y mira cómo responde el flujo.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField fullWidth size="small" placeholder="Hola, quiero información..." value={probarMsg} onChange={(e) => setProbarMsg(e.target.value)} />
            <Button variant="contained" onClick={probar}>Enviar</Button>
          </Stack>
          {probarResp.length > 0 && (
            <Paper sx={{ p: 2, bgcolor: '#ECE5DD' }}>
              <Stack spacing={1}>
                {probarResp.map((m, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Paper sx={{ p: 1, px: 1.5, bgcolor: '#fff', maxWidth: '85%' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m}</Typography>
                    </Paper>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProbarOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlujoEditor;
